import React from 'react';
import Modal from 'react-bootstrap/Modal';
import ServicesContext from '../../contexts/ServicesContext';
import { strings } from '../../resources/strings';
import { Recipe } from '../../models/Recipe';
import { LinkedCustomerRecipe } from '../../models/LinkedCustomerRecipe';
import { CustomerRecipe } from '../../models/CustomerRecipe';
import { ProductRecipeLinkingRequest } from '../../models/ProductRecipeLinkingRequest';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { RecipeList } from './RecipeList/RecipeList';
import { PairFrameLens } from '../../models/PairFrameLens';
import { PairsFrameLensGroupType } from '../../enums/PairsFrameLensGroupType';
import { Button } from '../../common-components/Button/Button';
import { ContainerButtons } from './styles';
import { CustomerRecipeLinkingForm } from './CustomerRecipeLinkingForm/CustomerRecipeLinkingForm';
import { CustomerRecipeHistory } from './CustomerRecipeHistory/CustomerRecipeHistory';
import { useAuth } from '../../contexts/AuthContext';
import { FormRecipeValues, RegisterRecipeForm } from './RegisterRecipeForm/RegisterRecipeForm';
import { RecipeTable } from '../../enums/RecipeTable';
import { RecipeType } from '../../enums/RecipeType';
import { RecipeOriginType } from '../../enums/RecipeOriginType';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { ProfileType } from '../../enums/ProfileType';
import { NewLooseCustomerRecipeInput } from '../../services/CustomerRecipes/NewLooseCustomerRecipe/NewLooseCustomerRecipeInput';
import { SaveDiagnosticInformationInputBase } from '../../models/SaveDiagnosticInformationInputBase';
import { DistanceType } from '../../enums/DistanceType';
import { DiagnosticInformation } from '../../models/DiagnosticInformation';
import { MultifocalInfo } from '../../models/MultifocalInfo';
import { SaveRecipeInputBase } from '../../models/SaveRecipeInputBase';
import { SaveProductRecipeLinkingRequestInput, SaveProductRecipeLinkingRequestInputItem } from '../../services/RecipeByProducts/SaveProductRecipeLinkingRequest/SaveProductRecipeLinkingRequestInput';
import { LinkRecipeWithCustomerRecipeInput } from '../../services/Recipes/LinkRecipeWithCustomerRecipe/LinkRecipeWithCustomerRecipeInput';
import { SaveRecipeInput } from '../../services/Recipes/SaveRecipe/SaveRecipeInput';

interface IModalRecipesForPhysicalStoreProps {
  visible: boolean;
  orderId?: number;
  orderNumber?: string;
  customerId?: number;
  customerShopifyId?: number;
  title: string;
  isCrossChannelSale?: boolean;
  currentStatePrescriptionId?: number;
  readonly?: boolean;
  close?: (orderId?: number) => void;
}

enum ModalContentType {
  List = 0,
  Linking = 1,
  CustomerRecipeHistory = 2,
  CustomerRecipeRegistration = 3,
  OrderRecipeRegistration = 4
}

const ModalRecipesForPhysicalStore: React.FC<IModalRecipesForPhysicalStoreProps> = ({
  visible,
  orderId,
  orderNumber,
  customerId,
  customerShopifyId,
  title,
  isCrossChannelSale,
  currentStatePrescriptionId,
  readonly,
  close
}) => {

  const { session } = useAuth();
  const [modalContentType, setModalContentType] = React.useState<ModalContentType>(ModalContentType.List);
  const [loading, setLoading] = React.useState<number>(0);
  const [recipes, setRecipes] = React.useState<Recipe[]>([]);
  const [customerRecipes, setCustomerRecipes] = React.useState<CustomerRecipe[]>([]);
  const [linkedCustomerRecipes, setLinkedCustomerRecipes] = React.useState<LinkedCustomerRecipe[]>([]);
  const [pairsFrameLens, setPairsFrameLens] = React.useState<PairFrameLens[]>([]);
  const [pairsFrameLensGroupType, setPairsFrameLensGroupType] = React.useState<PairsFrameLensGroupType | undefined | null>(null);
  const [formRecipeValues, setFormRecipeValues] = React.useState<FormRecipeValues | undefined>(undefined);
  const [linkingRequests, setLinkingRequests] = React.useState<ProductRecipeLinkingRequest[]>([]);

  const {
    toastService,
    alertService,
    getOrderRecipesAndLinkedCustomerRecipesService,
    getPairsFrameLensByOrderService,
    getCustomerRecipeHistoryByIdService,
    newLooseCustomerRecipeService,
    getDetailedStatusForOrderRecipeLinkingService,
    removeProductRecipeLinkingRequestService,
    getOrderLinkingRequestsService,
    saveProductRecipeLinkingRequestService,
    deleteRecipeService,
    linkRecipeWithCustomerRecipeService,
    saveRecipeService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    if (!visible) return;
    setLoading(4);
    getRecipesAndLinkedCustomerRecipes();
    getOrderLinkingRequests();
    getPairsFrameLens();
    getCustomerRecipes();
  }, [visible]);

  const orderIsCrossChannelSaleWithPrescriptionInAdvancedState = React.useMemo(() => {
    return isCrossChannelSale && currentStatePrescriptionId &&
      currentStatePrescriptionId > StatePrescriptionIdentification.ReceitaPendente &&
      session?.user?.profileType == ProfileType.Vendor;
  }, [isCrossChannelSale, currentStatePrescriptionId, session?.user?.profileType]);

  const recipesLocked = readonly || orderIsCrossChannelSaleWithPrescriptionInAdvancedState;

  const body = React.useMemo(() => {
    if (loading > 0) return <WaitingIcon />

    if (modalContentType == ModalContentType.OrderRecipeRegistration) {
      return (
        <RegisterRecipeForm
          orderId={orderId}
          orderNumber={orderNumber}
          recipeTable={RecipeTable.CustomerRecipe}
          session={session}
          values={formRecipeValues}
          change={(values) => changeFormRecipeValues(values)}
        />
      );
    }

    if (modalContentType == ModalContentType.CustomerRecipeRegistration) {
      return (
        <RegisterRecipeForm
          orderId={orderId}
          orderNumber={orderNumber}
          recipeTable={RecipeTable.CustomerRecipe}
          session={session}
          values={formRecipeValues}
          change={(values) => changeFormRecipeValues(values)}
        />
      );
    }

    if (modalContentType == ModalContentType.CustomerRecipeHistory) {
      return (
        <CustomerRecipeHistory
          recipes={customerRecipes}
        />
      )
    }

    if (modalContentType == ModalContentType.Linking) {
      return (
        <CustomerRecipeLinkingForm
          pairsFrameLens={pairsFrameLens}
          customerRecipes={customerRecipes}
          linkingRequests={linkingRequests}
          setLinkingRequests={setLinkingRequests}
        />
      );
    }

    return (
      <RecipeList
        orderRecipes={recipes}
        customerRecipes={customerRecipes}
        linkingRequests={linkingRequests}
        deleteLinking={recipesLocked ? undefined : deleteLinking}
        deleteRecipe={recipesLocked ? undefined : deleteRecipe}
      />
    );
  }, [
    modalContentType, loading, pairsFrameLensGroupType, linkedCustomerRecipes, pairsFrameLens, customerRecipes,
    session, formRecipeValues, linkingRequests, recipes, recipesLocked
  ]);

  const footer = React.useMemo(() => {
    if (loading > 0) return null;

    if (recipesLocked) {
      return null;
    }

    if (modalContentType == ModalContentType.OrderRecipeRegistration) {
      return (
        <Modal.Footer>
          <ContainerButtons>
            <Button text={strings.Cancel} minWidth='100px' marginRight='10px' click={() => setModalContentType(ModalContentType.List)} />
            <Button text={strings.Save} minWidth='100px' click={() => saveFormOrderRecipe()} />
          </ContainerButtons>
        </Modal.Footer>
      );
    }

    if (modalContentType == ModalContentType.CustomerRecipeRegistration) {
      return (
        <Modal.Footer>
          <ContainerButtons>
            <Button text={strings.Cancel} minWidth='100px' marginRight='10px' click={() => setModalContentType(ModalContentType.CustomerRecipeHistory)} />
            <Button text={strings.Save} minWidth='100px' click={() => saveFormCustomerRecipe()} />
          </ContainerButtons>
        </Modal.Footer>
      );
    }

    if (modalContentType == ModalContentType.CustomerRecipeHistory) {
      return (
        <Modal.Footer>
          <ContainerButtons>
            <Button text={strings.Cancel} minWidth='100px' marginRight='10px' click={() => setModalContentType(ModalContentType.Linking)} />
            <Button text={strings.NewRecipe} minWidth='100px' click={() => addNewCustomerRecipe()} />
          </ContainerButtons>
        </Modal.Footer>
      );
    }

    if (modalContentType == ModalContentType.Linking) {
      return (
        <Modal.Footer>
          <ContainerButtons>
            <Button text={strings.Cancel} minWidth='100px' marginRight='10px' click={() => cancelLinking()} />
            {pairsFrameLens.length > 0 ? (
              <Button text={strings.Save} minWidth='100px' marginRight='10px' click={() => saveLinking()} />
            ) : null}
            <Button text={strings.RecipeHistory} click={() => setModalContentType(ModalContentType.CustomerRecipeHistory)} />
          </ContainerButtons>
        </Modal.Footer>
      );
    }

    if ((pairsFrameLensGroupType == PairsFrameLensGroupType.GroupByUnmappedItems && (pairsFrameLens.length == 0 || pairsFrameLens.length > 1)) ||
      (!customerShopifyId || customerId == 6514) // cliente consumidor
    ) {
      return (
        <Modal.Footer>
          <ContainerButtons>
            <Button text={'Adicionar Receita'} click={() => addNewOrderRecipe()} />
          </ContainerButtons>
        </Modal.Footer>
      );
    }

    return (
      <Modal.Footer>
        <ContainerButtons>
          <Button text={'Receita por Grupo'} click={() => setModalContentType(ModalContentType.Linking)} />
        </ContainerButtons>
      </Modal.Footer>
    );
  }, [
    modalContentType, loading, pairsFrameLensGroupType, linkedCustomerRecipes, pairsFrameLens, customerRecipes,
    session, formRecipeValues, linkingRequests, recipes, recipesLocked
  ]);

  return (
    <Modal
      size="lg"
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      className="ModalRecipesForPhysicalStore"
    >
      <Modal.Header closeButton style={{ padding: '10px 1rem', height: '50px' }}>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='modalRecipeBody' style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px 1rem' }}>
        {body}
      </Modal.Body>

      {footer}
    </Modal>
  );

  function clickCloseModal() {
    setModalContentType(ModalContentType.List);
    close && close(orderId);
  }

  function getRecipesAndLinkedCustomerRecipes() {
    if (!orderId) return;
    getOrderRecipesAndLinkedCustomerRecipesService.handle({ orderId: orderId })
      .then(result => {
        setRecipes(result?.value?.recipes || []);
        setLinkedCustomerRecipes(result?.value?.linkedCustomerRecipes || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setLoading(loading => loading - 1);
      });
  }

  function getOrderLinkingRequests() {
    if (!orderId) return;
    getOrderLinkingRequestsService.handle({ orderId: orderId })
      .then(result => {
        setLinkingRequests(result?.value?.linkingRequests || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setLoading(loading => loading - 1);
      });
  }

  function getPairsFrameLens() {
    if (!orderId) return;
    getPairsFrameLensByOrderService.handle({ orderId: orderId, orderToken: '', considerFlatLenses: true })
      .then(result => {
        setPairsFrameLens(result?.value?.pairs || []);
        setPairsFrameLensGroupType(result?.value?.pairsFrameLensGroupType);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setLoading(loading => loading - 1);
      });
  }

  function getCustomerRecipes() {
    if (!customerId) {
      setLoading(loading => loading - 1);
      return;
    }
    getCustomerRecipeHistoryByIdService.handle({ customerId: customerId })
      .then(result => {
        setCustomerRecipes(result?.value?.recipes || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setLoading(loading => loading - 1);
      });
  }

  function changeFormRecipeValues(values: FormRecipeValues) {
    setFormRecipeValues(values);
  }

  function addNewCustomerRecipe() {
    setFormRecipeValues(undefined);
    setModalContentType(ModalContentType.CustomerRecipeRegistration);
  }

  function addNewOrderRecipe() {
    setFormRecipeValues(undefined);
    setModalContentType(ModalContentType.OrderRecipeRegistration);
  }

  async function saveFormCustomerRecipe() {
    if (!orderId) {
      toastService.warning(strings.UnidentifiedOrderToSaveTheRecipeYouNeedToIdentifyTheOrder);
      return;
    }
    if (!formRecipeValuesIsValid()) {
      alertService.warning(strings.ToSaveTheRecipeItIsNecessaryToAttachTheRecipeFileOrInformTheDiagnostic);
      return;
    }
    if (!formRecipeHeightsIsValid()) {
      return;
    }

    saveLooseCustomerRecipe();
  }

  async function saveFormOrderRecipe() {
    if (!orderId) {
      toastService.warning(strings.UnidentifiedOrderToSaveTheRecipeYouNeedToIdentifyTheOrder);
      return;
    }
    if (!formRecipeValuesIsValid()) {
      alertService.warning(strings.ToSaveTheRecipeItIsNecessaryToAttachTheRecipeFileOrInformTheDiagnostic);
      return;
    }
    if (!formRecipeHeightsIsValid()) {
      return;
    }

    saveOrderRecipe();
  }


  function formRecipeValuesIsValid() {
    if (!formRecipeValues) return false;
    if (
      formRecipeValues?.pathToFile ||
      formRecipeValues?.farDiagnostic?.leftSpherical || formRecipeValues?.farDiagnostic?.leftCylindrical ||
      formRecipeValues?.farDiagnostic?.leftAxis || formRecipeValues?.farDiagnostic?.leftNasoPupillaryDistance ||
      formRecipeValues?.farDiagnostic?.rightSpherical || formRecipeValues?.farDiagnostic?.rightCylindrical ||
      formRecipeValues?.farDiagnostic?.rightAxis || formRecipeValues?.farDiagnostic?.rightNasoPupillaryDistance ||
      formRecipeValues?.nearDiagnostic?.leftSpherical || formRecipeValues?.nearDiagnostic?.leftCylindrical ||
      formRecipeValues?.nearDiagnostic?.leftAxis || formRecipeValues?.nearDiagnostic?.leftNasoPupillaryDistance ||
      formRecipeValues?.nearDiagnostic?.rightSpherical || formRecipeValues?.nearDiagnostic?.rightCylindrical ||
      formRecipeValues?.nearDiagnostic?.rightAxis || formRecipeValues?.nearDiagnostic?.rightNasoPupillaryDistance
    ) return true;
    return false;
  }

  function formRecipeHeightsIsValid() {
    const leftEyeHeight = formRecipeValues?.leftEyeHeight ? +formRecipeValues.leftEyeHeight : null;
    const rightEyeHeight = formRecipeValues?.rightEyeHeight ? +formRecipeValues.rightEyeHeight : null;

    const allHeightsAreNull = !leftEyeHeight && !rightEyeHeight;
    if (allHeightsAreNull) return true;

    const informedLeftEyeHeightIsInvalid = leftEyeHeight && (leftEyeHeight < 0 || leftEyeHeight > 99);
    if (informedLeftEyeHeightIsInvalid) {
      toastService.warning(strings.ConfirmIfInformedFieldValueIsCorrect('Altura Pupila OE'));
      return false;
    }

    const informedRightEyeHeightIsInvalid = rightEyeHeight && (rightEyeHeight < 0 || rightEyeHeight > 99);
    if (informedRightEyeHeightIsInvalid) {
      toastService.warning(strings.ConfirmIfInformedFieldValueIsCorrect('Altura Pupila OD'));
      return false;
    }

    return true;
  }

  async function saveLooseCustomerRecipe() {
    const input = await createSaveLooseCustomerRecipeInput();

    if (!input) return;

    input.origin = RecipeOriginType.Sislog;

    newLooseCustomerRecipeService.handle(input)
      .then(result => {
        if (!result?.value?.result) return;
        handleSavedCustomerRecipeSuccessfully();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  async function saveOrderRecipe() {
    if (customerId && customerId > 0 && customerShopifyId && customerShopifyId > 0) {
      const input = await createSaveLooseCustomerRecipeInput();

      if (!input) return;

      input.origin = RecipeOriginType.Sislog;

      newLooseCustomerRecipeService.handle(input)
        .then(result => {
          if (!result?.value?.result) return;
          const inputLink = new LinkRecipeWithCustomerRecipeInput();
          inputLink.orderId = orderId!;
          inputLink.customerRecipeId = result.value.customerRecipe.id;
          linkRecipeWithCustomerRecipeService.handle(inputLink)
            .then(linkResult => {
              handleSavedOrderRecipeSuccessfully();
            }).catch(() => {
              toastService.warning(strings.OpsSomethingWrongSendTheData);
            })
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });
    } else {
      const input = await createSaveRecipeInput();

      if (!input) return;

      saveRecipeService.handle(input)
        .then(result => {
          if (!result?.value?.saveRecipeResult) return;
          handleSavedOrderRecipeSuccessfully();
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });
    }
  }

  async function createSaveRecipeInput(): Promise<SaveRecipeInput | null> {
    return new Promise(async (resolve, reject) => {
      if (!orderId) {
        resolve(null);
        return;
      }

      const inputBase = await createSaveRecipeInputBase();
      if (!inputBase) {
        resolve(null);
        return;
      }

      let input = new SaveRecipeInput();
      input = { ...input, ...inputBase };
      input.customerName = formRecipeValues?.name || '';
      input.customerEmail = '';
      input.orderId = orderId;
      input.recipeId = formRecipeValues?.id;

      resolve(input);
    });
  }

  async function createSaveLooseCustomerRecipeInput(): Promise<NewLooseCustomerRecipeInput | null> {
    return new Promise(async (resolve, reject) => {
      if (!customerShopifyId) {
        resolve(null);
        return;
      }

      const inputBase = await createSaveRecipeInputBase();
      if (!inputBase) {
        resolve(null);
        return;
      }

      let input = new NewLooseCustomerRecipeInput();
      input = { ...input, ...inputBase };
      input.name = formRecipeValues?.name || '';
      input.description = formRecipeValues?.description || '';
      input.customerShopifyId = customerShopifyId;
      input.customerRecipeId = formRecipeValues?.id;

      resolve(input);
    });
  }

  async function createSaveRecipeInputBase(): Promise<SaveRecipeInputBase | null> {
    return new Promise(async (resolve, reject) => {
      const input = new SaveRecipeInputBase();
      input.recipeType = formRecipeValues?.recipeType ? +formRecipeValues.recipeType : RecipeType.SingleVision;
      input.pathToFile = formRecipeValues?.pathToFile || '';
      input.recipeIsApproved = true; //formRecipeValues?.recipeApproved || false;
      input.nasoPupillaryDistanceIsApproved = true; //formRecipeValues?.nasoPupillaryDistanceApproved || false;
      input.userId = session?.user?.id;
      input.diagnosticInformations = [];
      input.multifocalInfo = formRecipeValues?.multifocalInfo;
      if (input.recipeType === RecipeType.Bifocal && !input.multifocalInfo) {
        input.multifocalInfo = new MultifocalInfo();
      }
      if (input.multifocalInfo) {
        input.multifocalInfo.actualBridge = input?.multifocalInfo?.actualBridge
          ? +input?.multifocalInfo?.actualBridge?.toString().trim() || undefined
          : undefined;
        input.multifocalInfo.actualEyeHeight = input?.multifocalInfo?.actualEyeHeight
          ? +input?.multifocalInfo?.actualEyeHeight.toString().trim() || undefined
          : undefined;
        input.multifocalInfo.actualEyeWidth = input?.multifocalInfo?.actualEyeWidth
          ? +input?.multifocalInfo?.actualEyeWidth.toString().trim() || undefined
          : undefined;
        input.multifocalInfo.actualLargestDiagonal = input?.multifocalInfo?.actualLargestDiagonal
          ? +input?.multifocalInfo?.actualLargestDiagonal.toString().trim() || undefined
          : undefined;
      }
      input.leftEyeHeight = formRecipeValues?.leftEyeHeight ? +formRecipeValues.leftEyeHeight : null;
      input.rightEyeHeight = formRecipeValues?.rightEyeHeight ? +formRecipeValues.rightEyeHeight : null;

      let farDiagnostic: SaveDiagnosticInformationInputBase | null = null;
      let nearDiagnostic: SaveDiagnosticInformationInputBase | null = null;

      if (formRecipeValues?.farDiagnostic) {
        if (!formRecipeValues.farDiagnostic.id) {
          formRecipeValues.farDiagnostic.createByName = session?.user?.name || '';
        }
        formRecipeValues.farDiagnostic.updateByName = session?.user?.name || '';
        formRecipeValues.farDiagnostic.diagnosticInformationId = formRecipeValues.farDiagnostic.id;
        formRecipeValues.farDiagnostic.distanceType = DistanceType.Far;
        input.diagnosticInformations.push({ ...formRecipeValues.farDiagnostic });
        farDiagnostic = formRecipeValues.farDiagnostic;
      }

      if (formRecipeValues?.nearDiagnostic) {
        if (!formRecipeValues.nearDiagnostic.id) {
          formRecipeValues.nearDiagnostic.createByName = session?.user?.name || '';
        }
        formRecipeValues.nearDiagnostic.updateByName = session?.user?.name || '';
        formRecipeValues.nearDiagnostic.diagnosticInformationId = formRecipeValues.nearDiagnostic.id;
        formRecipeValues.nearDiagnostic.distanceType = DistanceType.Near;
        input.diagnosticInformations.push({ ...formRecipeValues.nearDiagnostic });
        nearDiagnostic = formRecipeValues.nearDiagnostic;
      }

      if (input.recipeType === RecipeType.Bifocal && (farDiagnostic || nearDiagnostic)) {
        if (!nearDiagnosticBeenProvided(nearDiagnostic)) {
          const alertResult = await alertService
            .confirm('O diagnóstico para perto não foi fornecido. Posso inferir a partir do diagnóstico para longe?');

          if (alertResult.isConfirmed) {
            nearDiagnostic = getInferredNearDiagnosticFromFar(farDiagnostic, nearDiagnostic, input.multifocalInfo);

            const confirmResult = await alertService.confirmHtml(getDiagnosticConfirmationMessage(farDiagnostic, nearDiagnostic));
            if (confirmResult.isDenied) {
              resolve(null);
              return;
            }

            input.diagnosticInformations = input.diagnosticInformations.filter(x => x.distanceType === DistanceType.Far);
            input.diagnosticInformations.push(nearDiagnostic);
          }
        }
      }

      resolve(input);
    });
  }

  function nearDiagnosticBeenProvided(nearDiagnostic?: SaveDiagnosticInformationInputBase | null) {
    return nearDiagnostic?.leftSpherical || nearDiagnostic?.leftCylindrical ||
      nearDiagnostic?.leftAxis || nearDiagnostic?.leftNasoPupillaryDistance ||
      nearDiagnostic?.rightSpherical || nearDiagnostic?.rightCylindrical ||
      nearDiagnostic?.rightAxis || nearDiagnostic?.rightNasoPupillaryDistance;
  }

  function getInferredNearDiagnosticFromFar(
    farDiagnostic?: SaveDiagnosticInformationInputBase | null, nearDiagnostic?: SaveDiagnosticInformationInputBase | null,
    multifocalInfo?: MultifocalInfo
  ): SaveDiagnosticInformationInputBase {
    let value: number | null | undefined = 0;
    const inferredNearDiagnostic = nearDiagnostic ? { ...nearDiagnostic } : new DiagnosticInformation();
    const addField = getDiagnosticDecimalValue(multifocalInfo?.addField);
    const defaultValueForCalibratedNpdInference = -2;

    value = farDiagnostic?.rightSpherical ? getDiagnosticDecimalValue(farDiagnostic?.rightSpherical) + addField : null;
    inferredNearDiagnostic.rightSpherical = getFormattedDiagnosticValue(value);
    inferredNearDiagnostic.rightCylindrical = farDiagnostic?.rightCylindrical;
    inferredNearDiagnostic.rightAxis = farDiagnostic?.rightAxis;
    value = farDiagnostic?.rightNasoPupillaryDistance
      ? getDiagnosticDecimalValue(farDiagnostic?.rightNasoPupillaryDistance) + defaultValueForCalibratedNpdInference
      : null;
    inferredNearDiagnostic.rightNasoPupillaryDistance = getFormattedDiagnosticValue(value);

    value = farDiagnostic?.leftSpherical ? getDiagnosticDecimalValue(farDiagnostic?.leftSpherical) + addField : null;
    inferredNearDiagnostic.leftSpherical = getFormattedDiagnosticValue(value);
    inferredNearDiagnostic.leftCylindrical = farDiagnostic?.leftCylindrical;
    inferredNearDiagnostic.leftAxis = farDiagnostic?.leftAxis;
    value = farDiagnostic?.leftNasoPupillaryDistance
      ? getDiagnosticDecimalValue(farDiagnostic?.leftNasoPupillaryDistance) + defaultValueForCalibratedNpdInference
      : null;
    inferredNearDiagnostic.leftNasoPupillaryDistance = getFormattedDiagnosticValue(value);

    inferredNearDiagnostic.distanceType = DistanceType.Near;

    return inferredNearDiagnostic;
  }

  function getDiagnosticDecimalValue(strintValue?: string | null): number {
    return strintValue ? +strintValue.replaceAll(',', '.') : 0;
  }

  function getFormattedDiagnosticValue(decimalValue?: number | null): string {
    return decimalValue !== undefined && decimalValue !== null ? decimalValue.toFixed(2).toString().replaceAll('.', ',') : '';
  }

  function getDiagnosticConfirmationMessage(farDiagnostic?: SaveDiagnosticInformationInputBase | null, nearDiagnostic?: SaveDiagnosticInformationInputBase | null) {
    const thFixed = (content: string) => `<th style="width:100px;min-width:100px;">${content}</th>`;
    const th = (content: string) => `<th style="width:25%;">${content}</th>`;
    const td = (content: string) => `<td style="border:1px solid #ddd;">${content}</td>`;
    return `
      <table style="width:100%;">
          <thead>
              <tr>
                  ${thFixed('LONGE')}
                  ${th('ESF')}
                  ${th('CIL')}
                  ${th('EIXO')}
                  ${th('DNP')}
              </tr>
          </thead>
          <tbody>
              <tr>
                  ${td('OD')}
                  ${td(farDiagnostic?.rightSpherical || '')}
                  ${td(farDiagnostic?.rightCylindrical || '')}
                  ${td(farDiagnostic?.rightAxis || '')}
                  ${td(farDiagnostic?.rightNasoPupillaryDistance || '')}
              </tr>
              <tr>
                  ${td('OE')}
                  ${td(farDiagnostic?.leftSpherical || '')}
                  ${td(farDiagnostic?.leftCylindrical || '')}
                  ${td(farDiagnostic?.leftAxis || '')}
                  ${td(farDiagnostic?.leftNasoPupillaryDistance || '')}
              </tr>
          </tbody>
      </table>
      <br/>
      <table style="width:100%;">
          <thead>
              <tr>
                  ${thFixed('PERTO')}
                  ${th('ESF')}
                  ${th('CIL')}
                  ${th('EIXO')}
                  ${th('DNP')}
              </tr>
          </thead>
          <tbody>
              <tr>
                  ${td('OD')}
                  ${td(nearDiagnostic?.rightSpherical || '')}
                  ${td(nearDiagnostic?.rightCylindrical || '')}
                  ${td(nearDiagnostic?.rightAxis || '')}
                  ${td(nearDiagnostic?.rightNasoPupillaryDistance || '')}
              </tr>
              <tr>
                  ${td('OE')}
                  ${td(nearDiagnostic?.leftSpherical || '')}
                  ${td(nearDiagnostic?.leftCylindrical || '')}
                  ${td(nearDiagnostic?.leftAxis || '')}
                  ${td(nearDiagnostic?.leftNasoPupillaryDistance || '')}
              </tr>
          </tbody>
      </table>
      <br/>
      <p><strong>Confirma o diagnóstico acima?</strong></p>
    `;
  }

  function handleSavedCustomerRecipeSuccessfully() {
    setLoading(1);
    getCustomerRecipes();
    setModalContentType(ModalContentType.CustomerRecipeHistory);
  }

  function handleSavedOrderRecipeSuccessfully() {
    setLoading(3);
    getRecipesAndLinkedCustomerRecipes();
    getOrderLinkingRequests();
    getCustomerRecipes();
    setModalContentType(ModalContentType.List);
  }

  async function deleteLinking(linking: ProductRecipeLinkingRequest) {
    if (!orderId) return;

    if (orderIsCrossChannelSaleWithPrescriptionInAdvancedState) {
      const message = 'Não é possível remover receita por grupo para um pedido omnicanal depois de solicitar lentes. ' +
        'Fale com o time de operações do e-commerce ou com CH.';
      alertService.warning(message);
      return;
    }

    const detailedStatus = await getDetailedStatusForOrderRecipeLinkingService.handle({ orderId: orderId, considerFlatLensWhenGettingPairs: true });
    if (!detailedStatus?.value?.canChangeTheOrderRecipe) {
      const message = detailedStatus?.value?.prescriptionStateDescription
        ? `Não é possível remover receita por grupo quando o pedido está no estado "${detailedStatus?.value?.prescriptionStateDescription}".`
        : 'No estado corrente do pedido não é possível remover receitas por grupo.';
      alertService.warning(message);
      return;
    }

    const confirmation = await alertService.confirm(`Deseja realmente remover a receita do grupo?`);
    if (!confirmation.isConfirmed) return;

    removeProductRecipeLinkingRequestService.handle({
      productRecipeLinkingRequestId: linking.id,
      userId: session?.user?.id,
      processPrescriptionState: true
    }).then(result => {
      if (!result?.value?.result) return toastService.warning(strings.OpsSomethingWrongSendTheData);
      setLoading(2);
      getRecipesAndLinkedCustomerRecipes();
      getOrderLinkingRequests();
    }).catch(() => {
      toastService.warning(strings.OpsSomethingWrongSendTheData);
    });
  }

  function cancelLinking() {
    setLoading(1);
    getOrderLinkingRequests();
    setModalContentType(ModalContentType.List);
  }

  async function saveLinking() {
    if (!orderId) return;

    const resultGetPairs = await getPairsFrameLensByOrderService.handle({ orderId: orderId, orderToken: '', considerFlatLenses: true });
    if (!resultGetPairs?.value?.pairs || resultGetPairs.value.pairs.length === 0) {
      alertService.warning(strings.TheOrderDoesNotHaveFrameLensPairsToLink);
      return;
    }

    const resultGet = await getOrderLinkingRequestsService.handle({ orderId: orderId });
    const originalList = resultGet?.value?.linkingRequests || [];

    if (!reallyNeedToSave(originalList) ||
      existsAnyLinkingForSameItemsAndDifferentRecipes()
    ) return;

    const input = new SaveProductRecipeLinkingRequestInput();
    input.orderId = orderId;
    input.userId = session?.user?.id;
    input.linkingRequestItems = [];
    input.clientAreaRequisition = false;
    input.processPrescriptionState = true;

    if (linkingRequests.length > 0) {
      input.linkingRequestItems = linkingRequests.map(linkingRequest => {
        const inputItem = new SaveProductRecipeLinkingRequestInputItem();
        inputItem.frameSku = linkingRequest.frameSku;
        inputItem.lensSku = linkingRequest.lensSku;
        inputItem.customerRecipeId = linkingRequest.customerRecipeId;
        inputItem.orderItemIds = linkingRequest.items.map(lri => lri.orderItemId!);
        inputItem.pairNumber = linkingRequest.pairNumber;
        return inputItem;
      });
      input.confirmLinkingRequests = true;
    }

    saveProductRecipeLinkingRequestService.handle(input)
      .then(async resultSave => {
        const messages = resultSave?.value?.messages || [];
        const changes = (resultSave?.value?.deletedLinks || 0) + (resultSave?.value?.insertedLinks || 0) +
          (resultSave?.value?.updatedLinks || 0) + (resultSave?.value?.confirmedLinks || 0);
        if (messages.length > 0) await showReturnedMessagesFromSaveProductRecipeLinkingRequest(messages, changes === 0);
        handleLinkedProductRecipeSuccessfully();
      });
  }

  function reallyNeedToSave(originalList: ProductRecipeLinkingRequest[]) {
    if (originalList.length > 0 || linkingRequests.length > 0) return true;

    alertService.warningHtml(
      'De acordo com as escolhas realizadas não tem nada para salvar. ' +
      'Revise as suas escolhas e tente salvar novamente ou clique em Cancelar para sair.');

    return false;
  }

  function existsAnyLinkingForSameItemsAndDifferentRecipes() {
    if (linkingRequests.length < 1) return false;

    const listCustomerRecipeWithOrderItems = linkingRequests.map(lr => ({
      customerRecipeId: lr.customerRecipeId,
      orderItems: lr.items.map(lri => lri.orderItemId).join('-') + '-' + lr.pairNumber
    }));

    const linkingRequestsGroupByOrderItems: { orderItems: string, quantity: number }[] = [];

    listCustomerRecipeWithOrderItems.forEach(xlr => {
      const group = linkingRequestsGroupByOrderItems.find(g => g.orderItems === xlr.orderItems);
      if (group) {
        group.quantity += 1;
      } else {
        linkingRequestsGroupByOrderItems.push({ orderItems: xlr.orderItems, quantity: 1 });
      }
    });

    if (!linkingRequestsGroupByOrderItems.some(g => g.quantity > 1)) return false;

    alertService.warning(
      'O pedido possui pelo menos um grupo com mais de uma receita e isso é um problema. ' +
      'Tente resolver este problema e salvar novamente. Em caso de dúvidas avise o time Tech Zerezes.'
    );

    return true;
  }

  async function showReturnedMessagesFromSaveProductRecipeLinkingRequest(messages: string[], warning: boolean) {
    const message = messages.map((msg, idx) => `${idx > 0 ? '<hr>' : ''}` + `${msg}`).join('');
    return await warning
      ? alertService.warningHtml(message)
      : alertService.infoHtml(message);
  }

  async function handleLinkedProductRecipeSuccessfully() {
    setModalContentType(ModalContentType.List);
    setLoading(2);
    getRecipesAndLinkedCustomerRecipes();
    getOrderLinkingRequests();
  }

  async function deleteRecipe(recipeId: number) {
    if (!recipeId) return;

    const confirmation = await alertService.confirm(strings.DoYouReallyWantToDeleteTheRecipe);

    if (!confirmation.isConfirmed) return;

    setLoading(1);
    deleteRecipeService.handleWithExtraParams({ recipeId: recipeId }, true)
      .then(result => {
        if (result?.value?.result) {
          handleDeletedRecipeSuccessfully();
          return;
        }
        alertService.warning('Não foi possível excluir a receita!');
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      })
      .finally(() => {
        setLoading(loading => loading - 1);
      });
  }

  function handleDeletedRecipeSuccessfully() {
    setModalContentType(ModalContentType.List);
    setLoading(2);
    getRecipesAndLinkedCustomerRecipes();
    getOrderLinkingRequests();
  }
}

export { ModalRecipesForPhysicalStore };